import { MethodInterface } from '@campus/dal';
import { Pronunciation } from './pronunciation.interface';

export enum ExerciseType {
  Soundcorner = 'soundcorner',
  Wordbatch = 'wordbatch',
  Dialog = 'dialog',
}

/**
 * Per-type capability flags. The exercise list uses these to decide whether to
 * show the recording bottom bar and whether to auto-advance on result. Adding
 * a new ExerciseType requires adding a row here so the player picks up the
 * right behavior without having to grep for hardcoded type-checks.
 *
 * - `usesRecording`: drives the bottom recording bar (Soundcorner/Wordbatch
 *   record audio in-app; Dialog records inside the embedded webcomponent).
 * - `usesAutoAdvance`: when true, the player advances on every speech result
 *   without waiting for explicit "next" input from the recording bar. Only set
 *   for types where every result should advance the queue (e.g. Dialog, where
 *   one linguineo:end emission per question is the entire interaction).
 */
export const EXERCISE_CAPABILITIES: Record<ExerciseType, { usesRecording: boolean; usesAutoAdvance: boolean }> = {
  [ExerciseType.Soundcorner]: { usesRecording: true, usesAutoAdvance: false },
  [ExerciseType.Wordbatch]: { usesRecording: true, usesAutoAdvance: false },
  [ExerciseType.Dialog]: { usesRecording: false, usesAutoAdvance: true },
};

export interface Feedback {
  correct: {
    text?: string;
    audioUrl?: string;
  };
  incorrect: {
    text?: string;
    audioUrl?: string;
  };
}

export type Question = SoundcornerQuestion | WordbatchQuestion | DialogQuestion;

export interface BaseQuestionInterface {
  feedback?: Feedback;
}

export interface WordbatchQuestion extends BaseQuestionInterface {
  prompt?: Prompt;
  answers?: Answer[];
}

export interface SoundcornerQuestion extends BaseQuestionInterface {
  prompt?: Prompt;
}

export interface DialogQuestion extends BaseQuestionInterface {
  dialogSettings?: DialogSettings;
}

export interface DialogSettings {
  language?: string;
  tutorLanguage?: string;
  scenarioId: string;
  application?: string;

  answerType?:
    | 'MULTIPLE_CHOICE'
    | 'WRITING'
    | 'SPEAKING'
    | 'WRITING_AND_SPEAKING'
    | 'SPEAKING_WITH_INTERMEDIATE_WRITING';

  showFeedbackInConversations?: boolean;
  inlineFeedback?: boolean;
  showPronunciationFeedback?: boolean;
  doAdditionalPronunicationAnalysis?: boolean;
  showSimpleCorrectiveFeedback?: boolean;
  additionalCorrectiveFeedback?: boolean;
  enableContextualizedGrammarCorrections?: boolean;

  generateHintsForOpenFlow?: boolean;
  showTranslationsInConversation?: boolean;
  allowWordLookup?: boolean;

  alwaysWaitForBotToFinishSpeaking?: boolean;
  offerToLetBotProceedIfApplicable?: boolean;
  automaticProgressionIfTooManyMisses?: boolean;
  showFullBotResponses?: boolean;
  speakingRateOfBot?: number;

  speechRecognitionStrategy?: 'DEFAULT' | 'AUTOCORRECT_TO_CONTEXT';
  forgiveness?: number;

  switchToEasierBasedOnFrustration?: boolean;
  stopRecordingAutomaticallyOnSilence?: boolean;

  uiTheme?: 'light' | 'dark';
  animatedRewardIcons?: boolean;
  soundEffectsEnabled?: boolean;
  withBackButton?: boolean;
  blinkingCluesEnabled?: boolean;
  showProgressBar?: boolean;
  allowCorrectionsByUser?: boolean;
  botName?: string;
  userPhotoUrl?: string;
  botImageUrl?: string;
}

// Single source of truth for player + editor. Keep in sync with DialogSettings.
// `scenarioId` is omitted because it must come from the question and has no sensible default.
// `uiTheme` is intentionally omitted — Linguineo dark-theme support is unconfirmed; player
// falls back to light when undefined.
export const DEFAULT_DIALOG_SETTINGS: Omit<DialogSettings, 'scenarioId' | 'uiTheme'> = {
  application: 'DIE_KEURE',
  language: 'fr',
  tutorLanguage: 'nl',
  answerType: 'SPEAKING_WITH_INTERMEDIATE_WRITING',

  showFeedbackInConversations: true,
  inlineFeedback: false,
  showPronunciationFeedback: true,
  doAdditionalPronunicationAnalysis: true,
  showSimpleCorrectiveFeedback: true,
  additionalCorrectiveFeedback: true,
  enableContextualizedGrammarCorrections: true,

  generateHintsForOpenFlow: false,
  showTranslationsInConversation: false,
  allowWordLookup: true,

  alwaysWaitForBotToFinishSpeaking: true,
  offerToLetBotProceedIfApplicable: false,
  automaticProgressionIfTooManyMisses: false,
  showFullBotResponses: true,
  speakingRateOfBot: 1,

  speechRecognitionStrategy: 'DEFAULT',
  forgiveness: 0.08,

  switchToEasierBasedOnFrustration: false,
  stopRecordingAutomaticallyOnSilence: true,

  animatedRewardIcons: false,
  soundEffectsEnabled: true,
  withBackButton: false,
  blinkingCluesEnabled: false,
  showProgressBar: false,
  allowCorrectionsByUser: false,
  botName: 'Bot',
  userPhotoUrl: '',
  botImageUrl: '',
};

export interface QuestionQueueAmount {
  total: number;
  completed: number;
}

export interface Exercise {
  type: ExerciseType;
  title: string;
  subtitle?: string;
  rule?: Rule;
  globalFeedback: Feedback;
  questions: Question[];
  method?: MethodInterface;
}

export interface Rule {
  example: string;
  description: string;
}

export interface Prompt {
  text?: string;
  imageUrl?: string;
  imageAltText?: string;
  audioUrl?: string;
  confusion?: string;
  customConfusion?: Pronunciation[];
}

export interface Answer {
  label?: string;
  text?: string;
  isCorrect?: boolean;
  isActive?: boolean;
  words: Word[];
}

export interface Word {
  label: string;
  correctPronunciations: Pronunciation[];
  incorrectPronunciations: Pronunciation[];
  evaluate?: boolean;
  liaisonPhoneme?: string;
}
